import React, {useContext, useEffect} from 'react';
import {Card, CardBody} from 'reactstrap';
import ModelView from './ModelView';
import {IReduxModelConfigsState} from '@src/@interfaces';
import {ICache} from '@src/@interfaces/classes/Cacheable/ICache';
import Cache from '@src/classes/Cacheable/Cache';
import {IModelFormConfig} from '@src/@interfaces/components/Form/IModelFormConfig';
import app from '@src/helpers/app';
import {withRouterApi} from '@src/HOCs/withRouterApi';
import {FormViewProps} from '@src/@types/components/FormViewProps';
import {ListView} from './ListView';
import {BreadcrumbItem} from '@src/@types/components/Breadcrumbs';
import LoadingIndicator from '@src/components/LoadingIndicator/LoadingIndicator';
import Translate from '@src/components/Translate/Translate';
import {UserContext} from '@src/contexts/UserContext';
import FormModelProvider from '@src/components/Form/FormModelProvider';
import ErrorRepresentation from '@src/components/ErrorRepresentation/ErrorRepresentation';
import {UseQueryResult} from '@tanstack/react-query';
import {FormModelHttpResponse} from '@src/@types/components/Form';
import {IInteractiveFormConfig} from '@src/@interfaces/services/ConfigMaker/IInteractiveFormConfig';
import FormViewForm, {Props} from '@src/components/_forms/FormViewForm';
import getUri from '@src/helpers/getUri';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {getComponentName} from '@src/helpers/getComponentName';
import PageTitle from '@src/components/PageMetaTags/PageTitle';
import trans from '@src/helpers/trans';
import config from '@src/helpers/config';
import FormRemoteDataManager from './FormRemoteDataManager';
import {LayoutContext} from '@src/contexts/LayoutContext';

const emptyObject: Record<any, never> = {};

function SetPageTitleReplacersComponent<Model extends IEmptyModel = IEmptyModel>(props: any) {
    const {setPageMeta} = useContext(LayoutContext);
    const model = props?.model;

    useEffect(() => {
        if (model != null && setPageMeta) {
            setPageMeta({
                pageTitlesReplacers: {
                    ...model,
                    id: String(model?.id),
                }
            });
        }
    }, [setPageMeta, model]);

    return <FormRemoteDataManager<Model> {...props}/>;
}

export function CardContainer({children}: { children: React.ReactNode }) {
    return <Card>
        <CardBody>{children}</CardBody>
    </Card>;
}

export class FormView<
        FormModel extends IEmptyModel = IEmptyModel
> extends ModelView<FormViewProps<{ id: string }>, IModelFormConfig> {
    static configsContainerName: keyof Pick<IReduxModelConfigsState, 'listConfigs' | 'formConfigs'> = 'formConfigs';

    protected cache!: ICache<any>;
    protected cardContainerComponent: React.ComponentType<React.ComponentProps<typeof CardContainer>> = CardContainer;

    constructor(props: FormViewProps) {
        super(props);

        this.cache = new Cache();
    }

    componentWillUnmount() {
        super.componentWillUnmount();

        this.cache.flushCache();
    }

    protected renderUnconnectedToStoreException(message: string, exampleOfConnection: string | null = null) {
        exampleOfConnection ??= `FormView.getStoreConnector()(withRouterApi<FormViewProps>(FormView))`;

        return super.renderUnconnectedToStoreException(message, exampleOfConnection);
    }

    protected renderContent(): React.ReactNode {
        return <UserContext.Consumer>{userContext => {
            const permissions = this.getAccessPermissions();
            const context = this.getFormContextType();
            const accessDenied = !userContext?.canAny(permissions);

            let isReadOnly = accessDenied;
            if (context === 'update') {
                let updateModelPermissions = this.getConfigAccessPermissionsByContext('update');
                if (!updateModelPermissions) {
                    updateModelPermissions = app('names-resolver').permissions
                            .getUpdateRoutePermissionName(this.props.controllerName);
                }

                isReadOnly = !userContext?.canAny(updateModelPermissions);
            }

            return this.renderCard(accessDenied, isReadOnly);
        }}</UserContext.Consumer>;
    }

    protected renderCard(accessDenied: boolean, isReadOnly: boolean) {
        const errorHasOccurred = !this.getConfig() && !this.nowLoading();
        if (errorHasOccurred) {
            return <>
                <PageTitle title={trans('Exceptions.not_found') + ' | ' + config('client.app-name')}/>
                <Translate
                        message="Exceptions.configuration.model-form-configuration-file-not-found"
                        className="alert alert-danger py-5 text-center"
                        tag="div"
                />
            </>;
        }

        const Container = this.cardContainerComponent;

        return <Container>
            {this.renderCardBody(accessDenied, isReadOnly)}
        </Container>;
    }

    protected renderCardBody(accessDenied: boolean, isReadOnly: boolean) {
        if (this.nowLoading()) {
            return <div className="py-5 w-100 d-flex align-items-center justify-content-center text-muted">
                <LoadingIndicator type="chase" className="me-3"/>
                <Translate message="@common-phrases.loading"/>
            </div>;
        }

        if (this.getConfigError()) {
            return this.renderErrorRepresentation();
        }

        if (accessDenied) {
            return this.renderAccessDeniedError();
        }

        const ModelProvider = this.getFormModelProvider();

        return <ModelProvider<FormModel>
                requestUri={this.config.getUrlOfGetModelRequestEndpoint()}
                requestBody={{
                    'controller-name': this.props.controllerName,
                }}
                query={{
                    queryKey: app('queryKeys').getDetailsOfRecordQueryKey(
                            this.props.controllerName,
                            this.getUrlParams().id,
                    ),
                    options: {
                        useErrorBoundary: false,
                        keepPreviousData: !this.props.isIsolatedComponent,
                    }
                }}
        >
            {(queryResult) => this.renderForm(queryResult, isReadOnly)}
        </ModelProvider>;
    }

    protected renderForm(queryApi: UseQueryResult<FormModelHttpResponse<FormModel>, Error>, isReadOnly: boolean) {
        const {error, data, refetch} = queryApi;
        if (error) {
            return <ErrorRepresentation error={error} outsideContainer={true}/>;
        }

        const config = this.getConfig();
        if (!config) {
            return null;
        }

        const {model, options} = data ?? {};

        const modelListBreadcrumbItem = this.tryGetModelListBreadcrumbItem();
        const FormComponent = this.getFormComponent();

        const namesResolver = app('names-resolver').clientRoutes;

        return <SetPageTitleReplacersComponent<FormModel>
                queryApi={queryApi}
                componentToRender={FormComponent}
                {...this.getFormComponentProps()}
                isReadOnly={isReadOnly}
                config={config}
                context={this.getFormContextType()}
                formName={this.props.controllerName}
                model={model ?? (this.props.urlLocation.state as { model?: any })?.model}
                modelOptions={options ?? emptyObject}
                onCancel={this.props.onCancel}
                refetchModel={async () => {
                    await refetch();
                }}
                showFormUrl={this.getListConfig()?.update?.url ?? {
                    route: namesResolver.getShowRouteName(this.props.controllerName)
                }}
                cancelButtonHref={getUri(modelListBreadcrumbItem?.uri, {
                    route: namesResolver.getIndexRouteName(this.props.controllerName)
                })}
        />;
    }

    protected getFormModelProvider() {
        return FormModelProvider;
    }

    protected getFormComponent(): React.ComponentType<Props<FormModel>> {
        return FormViewForm;
    }

    protected getFormComponentProps(): Partial<Props<FormModel>> {
        return {};
    }

    protected getPageMetadataToUpdate() {
        const meta = super.getPageMetadataToUpdate();
        const listItem = this.getConfig() ?
                // method `tryGetModelListBreadcrumbItem` calls `loadConfig` method,
                // so we should avoid this until config of that model is loaded
                this.tryGetModelListBreadcrumbItem() :
                null;

        if (listItem) {
            meta.breadcrumbsItems ??= [];
            meta.breadcrumbsItems.unshift(listItem);
        }

        return meta;
    }

    protected getPageMetaFromConfig() {
        const config = this.getConfig();
        if (!config) {
            return null;
        }

        return config[this.getFormContextType()] ?? null;
    }

    protected getPageTitleFromConfig(): string | null {
        const contextConfig = this.getPageMetaFromConfig();
        if (!contextConfig) {
            return null;
        }

        return (typeof contextConfig === 'string') ?
                contextConfig :
                contextConfig.title;
    }

    protected getPageSubTitleFromConfig(): string | null {
        const contextConfig = this.getPageMetaFromConfig();
        if (!contextConfig || typeof contextConfig !== 'object' || !contextConfig.subtitle) {
            return null;
        }

        return contextConfig.subtitle;
    }

    protected tryGetModelListBreadcrumbItem(): BreadcrumbItem | null {
        const cacheKey = `model-list-title-of-${this.props.controllerName}-controller`;

        return this.cache.remember<BreadcrumbItem | null>(cacheKey, () => {
            if (!this.getUrlParams()) {
                return null;
            }

            const id = this.getUrlParams().id;
            const uri = this.props.urlLocation.pathname.replace(`/${id}`, '/list');

            const route = app('client-router').getRouteByUri(uri);
            const action = route?.getAction();
            if (!route || !action) {
                return null;
            }

            const repo = app('controllers');
            const parsedAction = repo.parseRouteAction(action);
            if (!parsedAction.controllerAction) {
                return null;
            }

            const controller = repo.getControllerByName(parsedAction.controllerName);
            if (!controller) {
                return null;
            }

            const listComponent = controller.getActionComponent(parsedAction.controllerAction);
            const pageTitleResolver = (
                    (listComponent as typeof ListView)?.getPageTitleByControllerName ||
                    (listComponent.constructor as typeof ListView)?.getPageTitleByControllerName
            );
            if (!pageTitleResolver) {
                return null;
            }

            const title = pageTitleResolver(this.props.controllerName);
            if (!title) {
                return null;
            }

            return {
                uri: route.resolveUrl(),
                label: title
            };
        });
    }

    protected isUpdatePageContext(): boolean {
        return !isNaN(+(this.getUrlParams()?.id ?? 'NaN'));
    }

    protected getFormContextType(): Extract<keyof IModelFormConfig, 'create' | 'update'> {
        return this.isUpdatePageContext() ? 'update' : 'create';
    }

    protected getConfigAccessPermissionsByContext(context: 'create' | 'update'): string | string[] | undefined {
        const formContextSettings = this.getConfig()?.[context];
        if (formContextSettings && typeof formContextSettings === 'object') {
            return formContextSettings.permissions;
        }

        return undefined;
    }

    protected getAccessPermissions(): string | string[] {
        const contextType = this.getFormContextType();

        const permissions = this.getConfigAccessPermissionsByContext(contextType);
        if (permissions) {
            return permissions;
        }

        const resolver = app('names-resolver').permissions;

        return contextType === 'update' ? [
            resolver.getShowRoutePermissionName(this.props.controllerName),
            resolver.getUpdateRoutePermissionName(this.props.controllerName),
        ] : [
            resolver.getCreateRoutePermissionName(this.props.controllerName),
        ];
    }

    protected getUrlParams() {
        if (!this.props.forceUrlParams) {
            return this.props.urlParams;
        }

        return {...this.props.urlParams, ...this.props.forceUrlParams};
    }

    protected get config(): IInteractiveFormConfig {
        return app('config-maker').makeFormConfig(
                this.props.controllerName,
                this.getConfig() ?? {},
                this.getUrlParams()
        );
    }

    protected isConnectedToStore(silent = false): boolean {
        if (typeof this.props.urlNavigate !== 'function') {
            if (silent) {
                return false;
            }

            throw new Error(`${getComponentName(this)} must be connected to router features!`);
        }

        return super.isConnectedToStore(silent);
    }

    static getPageTitleByControllerName(controllerName: string, hasModelId = false): string | null {
        const config = FormView.tryToGetConfigByControllerName<IModelFormConfig>(controllerName);
        const titleContainerPropertyName = hasModelId ? 'update' : 'create';
        if (!config || !config[titleContainerPropertyName]) {
            return null;
        }

        const title = config[titleContainerPropertyName];
        if (typeof title === 'string') {
            return title;
        }

        if (!title) {
            return null;
        }

        return title.title ?? null;
    }
}

export default FormView.getStoreConnector()(withRouterApi<FormViewProps>(FormView));
